// Ported from horizontal-actions-dashboard/frontend/src/features/terminals/TerminalDetailPage.tsx,
// MUI-styled. Reached only via direct navigation/links (not part of the Terminals tab menu).
// recharts usage (Pie/Cell/Legend/Tooltip/ResponsiveContainer/PieChart) is compatible with
// this app's recharts ^2.8.0 dependency — no API changes needed vs. the recharts v3 source.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import { getTerminalDetail, ApiError } from '../api/horizontalActionsApi';
import type { TerminalActionOut, TerminalDetailResponse } from '../api/horizontalActionsTypes';
import { TerminalsLoadingState, TerminalsErrorState } from './terminals-shared/TerminalsStateViews';
import { TerminalsProgressBar } from './terminals-shared/TerminalsProgressBar';
import { TerminalsPriorityBadge, TerminalsOpenHighPriorityFlag } from './terminals-shared/TerminalsPriorityBadge';
import { TerminalsStatusBadge, TerminalsApplicableBadge } from './terminals-shared/TerminalsStatusBadge';
import { STATUS_LABELS } from '../utils/horizontalActionsFormat';

const STATUS_COLORS: Record<string, string> = {
  completed: '#1e7e34',
  in_progress: '#0d47a1',
  not_started: '#616161',
  blocked_or_attention: '#c0392b',
  unknown: '#8a6d00',
};

function ActionsTable({
  caption,
  actions,
  showHighFlag,
}: {
  caption: string;
  actions: TerminalActionOut[];
  showHighFlag?: boolean;
}) {
  if (actions.length === 0) return <Typography>None.</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <caption style={{ position: 'absolute', left: -9999 }}>{caption}</caption>
        <TableHead>
          <TableRow>
            <TableCell>Priority</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Applicable</TableCell>
            <TableCell>Raw value</TableCell>
            <TableCell>ServiceNow ref</TableCell>
            <TableCell>Comments</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Data-quality flags</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {actions.map((a) => (
            <TableRow key={a.action_id} hover>
              <TableCell>
                <TerminalsPriorityBadge priority={a.priority} />
                {showHighFlag && a.priority === 'High' && (
                  <Box component="span" sx={{ ml: 0.5 }}>
                    <TerminalsOpenHighPriorityFlag />
                  </Box>
                )}
              </TableCell>
              <TableCell>{a.action_title}</TableCell>
              <TableCell>{a.category}</TableCell>
              <TableCell>
                <TerminalsStatusBadge status={a.normalized_status} />
              </TableCell>
              <TableCell>
                <TerminalsApplicableBadge applicable={a.applicable} />
              </TableCell>
              <TableCell>{a.raw_status ?? '—'}</TableCell>
              <TableCell>
                {a.task_url ? (
                  <a href={a.task_url} target="_blank" rel="noreferrer noopener">
                    {a.task_reference ?? 'Open'}
                  </a>
                ) : (
                  a.task_reference ?? '—'
                )}
              </TableCell>
              <TableCell>{a.comments ?? '—'}</TableCell>
              <TableCell>
                {a.source_sheet}!{a.source_cell}
              </TableCell>
              <TableCell>{a.data_quality_flags.length > 0 ? a.data_quality_flags.join(', ') : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function TerminalsTerminalDetail() {
  const { name } = useParams<{ name: string }>();
  const [data, setData] = useState<TerminalDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [showNotApplicable, setShowNotApplicable] = useState(false);

  const loadData = useCallback(async () => {
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getTerminalDetail(name);
      setData(result);
    } catch (err) {
      setError(err instanceof ApiError ? err : new Error('Failed to load terminal.'));
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <Box sx={{ p: 3 }}><TerminalsLoadingState label="Loading terminal…" /></Box>;
  if (error) return <Box sx={{ p: 3 }}><TerminalsErrorState error={error} onRetry={loadData} /></Box>;
  if (!data) return null;

  const { summary, open_high_priority, open_other, completed, not_applicable, unknown, status_distribution } = data;
  const pieData = Object.entries(status_distribution).map(([status, count]) => ({
    key: status,
    name: STATUS_LABELS[status] ?? status.replace(/_/g, ' '),
    value: count,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Link to="/terminals-summary">&larr; Back to dashboard</Link>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {summary.terminal_name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#5a6b7c', mb: 1 }}>
          Region: <Link to={`/terminals/regions/${summary.region_code}`}>{summary.region_display_name}</Link>
        </Typography>
        <TerminalsProgressBar pct={summary.completion_pct} label={`${summary.terminal_name} completion`} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Status distribution
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={80} label>
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={STATUS_COLORS[entry.key] ?? '#616161'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <Table size="small">
              <caption style={{ position: 'absolute', left: -9999 }}>
                Status distribution counts (accessible alternative to chart)
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pieData.map((entry) => (
                  <TableRow key={entry.key}>
                    <TableCell>{entry.name}</TableCell>
                    <TableCell>{entry.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Open High-priority actions
        </Typography>
        <ActionsTable caption="Open High-priority actions for this terminal" actions={open_high_priority} showHighFlag />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Other open actions
        </Typography>
        <ActionsTable caption="Other open actions for this terminal" actions={open_other} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Completed actions
        </Typography>
        <ActionsTable caption="Completed actions for this terminal" actions={completed} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Unknown or ambiguous records
        </Typography>
        <Typography variant="body2" sx={{ color: '#5a6b7c', mb: 1.5 }}>
          These records could not be confidently classified and are excluded from completion percentages.
        </Typography>
        <ActionsTable caption="Unknown or ambiguous records for this terminal" actions={unknown} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Button variant="outlined" size="small" onClick={() => setShowNotApplicable((v) => !v)}>
          {showNotApplicable ? 'Hide' : 'Show'} not-applicable records ({not_applicable.length})
        </Button>
        {showNotApplicable && (
          <Box sx={{ mt: 1.5 }}>
            <ActionsTable caption="Not-applicable records for this terminal" actions={not_applicable} />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
